import { FC, useMemo, useState } from 'react'

import styled from 'styled-components'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'

import { useSourceBusterEvent } from 'src/Hooks/useSourceBusterEvent'
import { DesktopTabletTrackSearch } from 'src/Pages/Artist/CreateCampaignPage/Components/TrackSearch/Components/DesktopTabletTrackSearch'
import { MobileTrackSearch } from 'src/Pages/Artist/CreateCampaignPage/Components/TrackSearch/Components/MobileTrackSearch'
import {
  SearchTrackFormData,
  SearchedTrack,
} from 'src/Pages/Artist/CreateCampaignPage/Components/TrackSearch'
import { ChosenTrack } from 'src/Components/ChosenTrack'
import { AdsFAQ } from 'src/Components/AdsFAQ'
import {
  AdCampaignStrategyEnum,
  useCreateAdCampaignMutation,
} from 'src/Redux/ad-campaignApi'
import { CampaignPlatformTypeEnum } from 'src/Types/campaigns'

import { TrackSearchError } from '../../../Components/TrackSearchError'

import { usePlaylistSearch } from './usePlaylistSearch'

const Container = styled.div`
  display: flex;
  margin: 0 auto;
  width: 100%;
  max-width: 960px;
  padding: 24px;
  flex-direction: column;
  gap: 24px;
  align-self: stretch;

  border-radius: 16px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  box-shadow: ${({ theme }) => theme.shadow.card};
`
const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  line-height: 32px;
  color: ${({ theme }) => theme.color.darkGrey};
`

export const SearchPlaylistForm: FC = () => {
  const navigate = useNavigate()
  const { sendCreateBasicCampaignSourceBusterEvent } = useSourceBusterEvent()
  const [createAdCampaign, { isLoading: isCreating }] =
    useCreateAdCampaignMutation()
  const { searchPlaylist, isLoading, playlists, error, onClearPlaylist } =
    usePlaylistSearch()
  const [playlist, setPlaylist] = useState<SearchedTrack | null>(null)
  const { control, reset } = useForm<SearchTrackFormData>({
    mode: 'onChange',
  })
  const onItemClick = (_: string, item?: SearchedTrack): void => {
    if (item) {
      setPlaylist(item)
    }
  }
  const onOutsideClick = (): void => {
    setPlaylist(null)
  }

  const handleCreate = (): void => {
    if (playlist) {
      createAdCampaign({
        resourceName: playlist.name,
        resourceSpotifyId: playlist.id,
        coverUrl: playlist.trackCoverUrl,
        strategy: AdCampaignStrategyEnum.PLAYLIST,
        ownerName: playlist.artistName,
        resourceSpotifyUrl: playlist.resourceSpotifyUrl ?? '',
        ownerSpotifyUrl: playlist.ownerSpotifyUrl ?? '',
      })
        .unwrap()
        .then((data) => {
          sendCreateBasicCampaignSourceBusterEvent(
            data.id,
            CampaignPlatformTypeEnum.META,
          )
          setPlaylist(null)
          navigate(`/campaigns/scheduleAds/${data.id}?step=settings`)
        })
    }
  }
  const onChangeClick = (): void => {
    setPlaylist(null)
    reset()
    onClearPlaylist()
  }

  const searchError = useMemo(() => {
    if (error) {
      return <TrackSearchError>{error}</TrackSearchError>
    }
    return null
  }, [error])

  if (playlist) {
    return (
      <ChosenTrack
        createLoading={isCreating}
        artist={playlist?.artistName}
        title={'New Meta Ads Campaign'}
        duration={playlist?.trackDuration}
        trackName={playlist?.name}
        trackUrl={playlist?.previewUrl}
        image={playlist?.trackCoverUrl}
        onChangeClick={onChangeClick}
        onCreateClick={handleCreate}
        changeButtonText='Change Playlist'
      />
    )
  }
  return (
    <>
      <Container>
        <Title>New Meta Ads Campaign</Title>
        <DesktopTabletTrackSearch
          isLoading={isLoading}
          onItemClick={onItemClick}
          tracks={playlists}
          onSearch={searchPlaylist}
          onOutsideClick={onOutsideClick}
          placeholder={'Enter playlist URL from Spotify'}
          control={control}
          error={searchError}
        />

        <MobileTrackSearch
          isLoading={isLoading}
          onItemClick={onItemClick}
          tracks={playlists}
          onSearch={searchPlaylist}
          placeholder={
            'Provide the link to your Spotify playlist that you want to promote'
          }
          control={control}
          error={searchError}
        />
      </Container>
      <AdsFAQ />
    </>
  )
}
